import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Input, Spin, Typography, message } from "antd";
import {
  CheckCircleOutlined,
  MessageOutlined,
  ReloadOutlined,
  RobotOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { startAgent, chatWithAgent, mockAnswer, resetAllData } from "../api";
import "./AgentChat.css";

const { TextArea } = Input;

interface ChatMessage {
  role: "agent" | "user";
  content: string;
}

export const AgentChat: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState("");

  const historyRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const node = historyRef.current;

      if (node) {
        node.scrollTo({
          top: node.scrollHeight,
          behavior: "smooth",
        });
      }
    });
  }, []);

  const handleStartAgent = useCallback(async () => {
    setLoading(true);
    setHistoryLoading(true);
    setError("");
    setMessages([]);
    setThreadId(null);

    try {
      const result = await startAgent();

      setThreadId(result.threadId);

      if (result.message) {
        setMessages([
          {
            role: "agent",
            content: result.message,
          },
        ]);
      }
    } catch (err: unknown) {
      messageApi.error(err instanceof Error ? err.message : "启动智能分组测试助手失败");
    } finally {
      setLoading(false);
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    handleStartAgent();
  }, [handleStartAgent]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, error, scrollToBottom]);

  const handleSend = async (overrideContent?: string) => {
    const content = (overrideContent ?? input).trim();

    if (!content || !threadId || loading || historyLoading) {
      return;
    }

    setInput("");
    setError("");

    setMessages((previous) => [
      ...previous,
      {
        role: "user",
        content,
      },
    ]);

    setLoading(true);

    try {
      const result = await chatWithAgent(threadId, content);

      setMessages((previous) => [
        ...previous,
        {
          role: "agent",
          content: result.message,
        },
      ]);
    } catch (err: unknown) {
      messageApi.error(err instanceof Error ? err.message : "对话失败");
    } finally {
      setLoading(false);
    }
  };

  const handleSimulateStudentAnswer = async () => {
    setLoading(true);
    try {
      const result = await mockAnswer();
      // 如果返回了 message 字段，视为成功提示
      if (result.message) {
        messageApi.info(result.message);
      }
    } catch (err: unknown) {
      messageApi.error(err instanceof Error ? err.message : "模拟学生回答失败");
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllData = async () => {
    if (!threadId) {
      return;
    }
    try {
      const result = await resetAllData(threadId);
      // 如果返回了 message 字段，视为成功提示
      if (result.message) {
        messageApi.info(result.message);
      }
    } catch (err: unknown) {
      messageApi.error(err instanceof Error ? err.message : "重置所有数据失败");
    }
  };

  const handleInputKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

return (
  <div className="agent-chat">
    {contextHolder}
    <div className="agent-layout">
      {/* 左侧菜单 */}
      <aside className="agent-sidebar">
        <div className="agent-sidebar-brand">
          <div className="agent-sidebar-brand-icon">
            <MessageOutlined />
          </div>
          <span>教学管理</span>
        </div>

        <nav className="agent-menu">
          <button
            type="button"
            className="agent-menu-item agent-menu-item-active"
          >
            <RobotOutlined />
            <span>智能分组测试助手</span>
          </button>
        </nav>
      </aside>

      {/* 右侧对话区域 */}
      <main className="agent-main">
        <section className="phyanim-chat">
          <header className="phyanim-topbar">
            <div className="phyanim-title-wrapper">
              <div className="phyanim-title-icon">
                <MessageOutlined />
              </div>

              <div>
                <Typography.Title level={3}>
                  对话空间
                </Typography.Title>

                <Typography.Text type="secondary">
                  面向物理课堂的智能分组测试助手
                </Typography.Text>
              </div>
            </div>

            <div className="phyanim-topbar-actions">
              <div
                className={`phyanim-status ${
                  loading ? "is-loading" : ""
                } ${error ? "is-error" : ""}`}
              >
                {error ? (
                  <span className="phyanim-status-dot error" />
                ) : loading ? (
                  <span className="phyanim-status-dot loading" />
                ) : (
                  <CheckCircleOutlined />
                )}

                <span>
                  {error ? "Error" : loading ? "Thinking" : "Ready"}
                </span>
              </div>

              <Button
                icon={<ReloadOutlined />}
                onClick={handleSimulateStudentAnswer}
                className="phyanim-reset"
              >
                学生模拟作答
              </Button>
              
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetAllData}
                className="phyanim-reset"
              >
                重置所有数据
              </Button>
            </div>
          </header>

          {/* 只有这个区域滚动 */}
          <div className="phyanim-history" ref={historyRef}>
            {historyLoading && (
              <div className="phyanim-history-loading">
                <Spin size="large" />
                <Typography.Text type="secondary">
                  正在启动智能分组测试助手...
                </Typography.Text>
              </div>
            )}

            {!historyLoading &&
              messages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`phyanim-message ${
                    message.role === "user"
                      ? "phyanim-message-user"
                      : "phyanim-message-assistant"
                  }`}
                >
                  <div className="phyanim-avatar">
                    {message.role === "agent" ? (
                      <RobotOutlined />
                    ) : (
                      <UserOutlined />
                    )}
                  </div>

                  <div className="phyanim-message-body">
                    <div className="phyanim-message-meta">
                      <span>
                        {message.role === "agent" ? "智能分组测试助手" : "我"}
                      </span>

                      {message.role === "agent" && (
                        <small>
                          <CheckCircleOutlined />
                          已完成
                        </small>
                      )}
                    </div>

                    <div className="phyanim-bubble">
                      {message.content}
                    </div>
                  </div>
                </div>
              ))}

            {!historyLoading && loading && (
              <div className="phyanim-message phyanim-message-assistant">
                <div className="phyanim-avatar">
                  <RobotOutlined />
                </div>

                <div className="phyanim-message-body">
                  <div className="phyanim-message-meta">
                    <span>智能分组测试助手</span>
                    <small className="is-running">正在思考</small>
                  </div>

                  <div className="phyanim-bubble phyanim-typing-bubble">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="phyanim-error">
                {error}
              </div>
            )}
          </div>

          {/* 输入框固定在底部 */}
          <footer className="phyanim-input-panel">
            <div className="phyanim-quick-tags">
              {[
                "当前都有哪些测试",
                "创建分组测试",
                "查看下最近的测试题",
                "分析下最近一次测试的学生成绩",
                "第一次测试",
                "牛顿运动定律和刚体力学",
                "10",
                "是",
                "否"
              ].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="phyanim-quick-tag"
                  disabled={loading || historyLoading}
                  onClick={() => handleSend(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>

            <TextArea
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
              }}
              onKeyDown={handleInputKeyDown}
              autoSize={{
                minRows: 2,
                maxRows: 5,
              }}
              disabled={loading || historyLoading}
              placeholder="描述你的需求..."
            />

            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={loading}
              disabled={!input.trim() || historyLoading}
              onClick={() => handleSend()}
              className="phyanim-send"
            >
              发送
            </Button>
          </footer>
        </section>
      </main>
    </div>
  </div>
);
}